import type { Change } from 'prosemirror-changeset';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	ColorScheme as PublicColorScheme,
	DiffStepAttribution,
} from '../../../showDiffPluginType';

import type { ColorScheme, DiffAccentColor } from './types';

export type DiffAttributionSpanData = DiffStepAttribution & {
	stepIndex: number;
};

const ATTRIBUTION_COLOR_PALETTE: DiffAccentColor[] = [
	'purple',
	'blue',
	'teal',
	'magenta',
	'orange',
	'green',
];

/** `userId` is primary; `agentId`, or `agentType` as its fallback, separates shared users. */
export const getAttributionKey = (
	attribution: DiffStepAttribution | undefined,
): string | undefined => {
	const userId = attribution?.userId?.trim();
	const agentId = attribution?.agentId?.trim();
	const agentType = attribution?.agentType?.trim();

	if (!userId && !agentId && !agentType) {
		return undefined;
	}

	const agentIdentity = agentId || (agentType ? `type:${agentType}` : '');

	return `user:${userId ?? ''}|agent:${agentIdentity}`;
};

export const areAttributionColorGatesEnabled = (): boolean =>
	fg('confluence_ncs_step_diffing_version_history') &&
	isExperimentEnabled('platform_editor_show_diff_color_scheme_refactor');

export const isAttributionColoringEnabled = (
	stepAttributions: Array<DiffStepAttribution | undefined> | undefined,
): boolean => {
	if (!areAttributionColorGatesEnabled() || !stepAttributions?.length) {
		return false;
	}

	const attributionKeys = new Set(
		stepAttributions.map(getAttributionKey).filter((key): key is string => key !== undefined),
	);

	return attributionKeys.size > 1;
};

export const createAttributionColorMap = (
	stepAttributions: Array<DiffStepAttribution | undefined>,
): Map<string, DiffAccentColor> => {
	const keys = Array.from(
		new Set(
			stepAttributions.map(getAttributionKey).filter((key): key is string => key !== undefined),
		),
	).sort();

	return new Map(
		keys.map((key, index) => [
			key,
			ATTRIBUTION_COLOR_PALETTE[index % ATTRIBUTION_COLOR_PALETTE.length],
		]),
	);
};

const getLatestAttribution = (changes: Change[]): DiffAttributionSpanData | undefined => {
	const data = changes
		.flatMap((change) => [...change.deleted, ...change.inserted])
		.map((span) => span.data)
		.filter(
			(value): value is DiffAttributionSpanData =>
				typeof value === 'object' &&
				value !== null &&
				'stepIndex' in value &&
				typeof value.stepIndex === 'number',
		);

	return data.reduce<DiffAttributionSpanData | undefined>(
		(latest, attribution) =>
			!latest || attribution.stepIndex > latest.stepIndex ? attribution : latest,
		undefined,
	);
};

const rangesOverlap = (fromA: number, toA: number, fromB: number, toB: number): boolean =>
	fromA < toB && fromB < toA;

const changesOverlap = (change: Change, attributedChange: Change): boolean => {
	const insertedOverlap =
		change.inserted.length > 0 &&
		attributedChange.inserted.length > 0 &&
		rangesOverlap(change.fromB, change.toB, attributedChange.fromB, attributedChange.toB);
	const deletedOverlap =
		change.deleted.length > 0 &&
		attributedChange.deleted.length > 0 &&
		rangesOverlap(change.fromA, change.toA, attributedChange.fromA, attributedChange.toA);

	return insertedOverlap || deletedOverlap;
};

export const getColorSchemeForChange = (
	change: Change,
	attributedChanges: Change[],
	attributionColors: Map<string, DiffAccentColor>,
	fallback: PublicColorScheme | undefined,
): ColorScheme | undefined => {
	const key = getAttributionKey(
		getLatestAttribution(
			attributedChanges.filter((attributedChange) => changesOverlap(change, attributedChange)),
		),
	);
	return key ? (attributionColors.get(key) ?? fallback) : fallback;
};
